import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

export const SignalSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const lines = [
    "We live in the loudest era in human history.",
    "Every moment competes for our attention.",
    "Messages arrive faster than meaning.",
    "Opinions multiply faster than wisdom.",
    "Noise surrounds us.",
  ];

  const consequences = [
    "But the greatest threat of noise is not that it is loud.",
    "It is that it slowly erodes our ability to hear what matters.",
    "Clarity disappears.",
    "Judgment weakens.",
    "Decisions become reactive.",
    "And leaders begin to move quickly without ever truly seeing.",
  ];

  return (
    <section
      data-testid="signal-section"
      ref={ref}
      className="py-16 md:py-24 lg:py-32 bg-[#1F2A44] relative overflow-hidden"
    >
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      <div className="container-peak relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="section-tag block mb-4">Our Manifesto</span>
          </motion.div>

          {/* First part - the problem */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8 }}
            className="mb-12 lg:mb-16"
          >
            {lines.map((line, index) => (
              <motion.p
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="text-[#F4F1EC]/90 text-lg md:text-xl lg:text-2xl font-light leading-relaxed mb-3 md:mb-4 text-center lg:text-left"
              >
                {line}
              </motion.p>
            ))}
          </motion.div>

          {/* Second part - consequences */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mb-12 lg:mb-16 pl-4 lg:pl-6 border-l-2 border-[#D97904]"
          >
            {consequences.map((line, index) => (
              <motion.p
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.12 }}
                className="text-[#F4F1EC]/70 text-base md:text-lg lg:text-xl font-light leading-relaxed mb-2 md:mb-3"
              >
                {line}
              </motion.p>
            ))}
          </motion.div>

          {/* The solution */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.6 }}
            className="text-center pt-6 lg:pt-8"
          >
            <p className="text-[#A7B5A6] text-base lg:text-lg mb-4">
              Great performance has never come from noise.
            </p>
            <p className="text-[#F4F1EC] text-lg md:text-xl lg:text-2xl font-light mb-2">
              The greatest athletes, artists, and leaders share a common discipline.
            </p>
            <p className="text-[#D97904] text-xl md:text-2xl lg:text-3xl font-['Cormorant_Garamond'] mt-6 lg:mt-8">
              They engineer peak performance with well-being.
            </p>
            <p className="text-[#D97904] text-xl md:text-2xl lg:text-3xl font-['Cormorant_Garamond']">
              They engineer <em className="italic">signal over noise</em>.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SignalSection;
